/**
 * Périodes et formats — fuseau Africa/Bamako (UTC+0 toute l'année, sans changement d'heure ; calculé par `Intl` plutôt que codé en dur,
 * pour rester juste si la règle changeait). Toute heure SAISIE ou AFFICHÉE dans l'écran Historique est une heure de Bamako, quel que soit
 * le fuseau du navigateur de la personne (un dispatcher en déplacement voit les heures de la flotte, pas les siennes).
 */
export const FUSEAU = 'Africa/Bamako'

const JOUR_MS = 86_400_000

function parties(ms: number): Record<string, number> {
  const f = new Intl.DateTimeFormat('en-GB', {
    timeZone: FUSEAU, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  const o: Record<string, number> = {}
  for (const p of f.formatToParts(new Date(ms))) if (p.type !== 'literal') o[p.type] = Number(p.value)
  return o
}

/** Décalage (ms) du fuseau de Bamako par rapport à UTC à cet instant : 0 aujourd'hui. */
export function decalageMs(ms: number): number {
  const p = parties(ms)
  return Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!, p.second!) - Math.floor(ms / 1000) * 1000
}

/** Minuit (heure de Bamako) du jour qui contient `ms`. */
export function debutDeJour(ms: number): number {
  const p = parties(ms)
  const local = Date.UTC(p.year!, p.month! - 1, p.day!)
  return local - decalageMs(ms)
}

export type PresetPeriode = 'aujourdhui' | 'hier' | '7j'
export const LIBELLE_PRESET: Record<PresetPeriode, string> = { aujourdhui: "Aujourd'hui", hier: 'Hier', '7j': '7 jours' }

/** Aujourd'hui = de minuit à maintenant ; hier = la journée d'hier entière ; 7 jours = les 7 derniers jours glissants. */
export function periodePreset(preset: PresetPeriode, maintenant: number): { du: number; au: number } {
  const minuit = debutDeJour(maintenant)
  if (preset === 'aujourdhui') return { du: minuit, au: maintenant }
  if (preset === 'hier') return { du: debutDeJour(minuit - 1), au: minuit }
  return { du: maintenant - 7 * JOUR_MS, au: maintenant }
}

const deux = (n: number) => String(n).padStart(2, '0')

/** Valeur d'un `<input type="datetime-local">` pour cet instant, en heure de Bamako (« 2026-10-14T08:30 »). */
export function versChampLocal(ms: number): string {
  const p = parties(ms)
  return `${p.year}-${deux(p.month!)}-${deux(p.day!)}T${deux(p.hour!)}:${deux(p.minute!)}`
}

/** Instant (ms) d'une valeur de `datetime-local` lue comme une heure de Bamako ; `null` si illisible. */
export function depuisChampLocal(valeur: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(valeur.trim())
  if (!m) return null
  const [, a, mo, j, h, mi, s] = m
  const local = Date.UTC(Number(a), Number(mo) - 1, Number(j), Number(h), Number(mi), Number(s ?? 0))
  if (!Number.isFinite(local)) return null
  // Le décalage dépend de l'instant : on l'évalue au premier jet puis on le confirme.
  const premier = local - decalageMs(local)
  return local - decalageMs(premier)
}

export interface VerdictPeriode {
  ok: boolean
  /** Période à demander (ramenée à la rétention, au plus `maxJours`). */
  du: number
  au: number
  /** Messages à montrer à la personne ; `ok = false` ⇒ au moins un. */
  messages: string[]
  tronqueeParRetention: boolean
}

/**
 * Contrôle d'une période AVANT l'appel : la base refuse (> 31 jours, fin avant début) et ramène à la rétention du palier ; ici on le
 * dit à l'écran d'avance, pour ne jamais présenter comme « tout l'historique » ce que la rétention a tronqué.
 */
export function verifierPeriode(
  du: number | null,
  au: number | null,
  limites: { maxJours: number; retentionJours: number; maintenant: number },
): VerdictPeriode {
  const messages: string[] = []
  if (du === null || au === null) return { ok: false, du: 0, au: 0, messages: ['Choisissez un début et une fin de période.'], tronqueeParRetention: false }
  if (au <= du) return { ok: false, du, au, messages: ['La fin de la période doit suivre son début.'], tronqueeParRetention: false }
  if (au - du > limites.maxJours * JOUR_MS) {
    return { ok: false, du, au, messages: [`La période ne peut pas dépasser ${limites.maxJours} jours par demande.`], tronqueeParRetention: false }
  }
  const plancher = limites.maintenant - limites.retentionJours * JOUR_MS
  let tronquee = false
  let duEff = du
  let auEff = au
  if (du < plancher) {
    tronquee = true
    duEff = plancher
    auEff = Math.max(au, plancher)
    messages.push(
      auEff <= duEff
        ? `Aucune donnée n'est conservée avant le ${formatDateHeure(plancher)} (${limites.retentionJours} jours).`
        : `Seuls les ${limites.retentionJours} derniers jours sont conservés : la période est ramenée au ${formatDateHeure(plancher)}.`,
    )
  }
  return { ok: true, du: duEff, au: auEff, messages, tronqueeParRetention: tronquee }
}

/* ─────────────────────────── Formats (toujours en heure de Bamako) ─────────────────────────── */

const fmtHeure = new Intl.DateTimeFormat('fr-FR', { timeZone: FUSEAU, hour: '2-digit', minute: '2-digit' })
const fmtHeureS = new Intl.DateTimeFormat('fr-FR', { timeZone: FUSEAU, hour: '2-digit', minute: '2-digit', second: '2-digit' })
const fmtJour = new Intl.DateTimeFormat('fr-FR', { timeZone: FUSEAU, day: '2-digit', month: '2-digit', year: 'numeric' })
const fmtJourCourt = new Intl.DateTimeFormat('fr-FR', { timeZone: FUSEAU, weekday: 'short', day: '2-digit', month: '2-digit' })

export const formatHeure = (ms: number) => fmtHeure.format(new Date(ms))
export const formatHeureSeconde = (ms: number) => fmtHeureS.format(new Date(ms))
export const formatJour = (ms: number) => fmtJour.format(new Date(ms))
export const formatJourCourt = (ms: number) => fmtJourCourt.format(new Date(ms))
export const formatDateHeure = (ms: number) => `${fmtJour.format(new Date(ms))} ${fmtHeure.format(new Date(ms))}`

/** « 2 h 05 », « 12 min », « 45 s », « 0 s ». */
export function formatDuree(secondes: number): string {
  const s = Math.max(0, Math.round(secondes))
  if (s < 60) return `${s} s`
  const min = Math.floor(s / 60)
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  const reste = min % 60
  return reste === 0 ? `${h} h` : `${h} h ${deux(reste)}`
}

/** Distance : « 850 m » sous le kilomètre, sinon « 12,4 km ». */
export function formatDistance(metres: number): string {
  if (!Number.isFinite(metres)) return '—'
  if (metres < 1000) return `${Math.round(metres)} m`
  return `${(metres / 1000).toFixed(metres < 100_000 ? 1 : 0).replace('.', ',')} km`
}

export const formatVitesse = (kmh: number | null | undefined) => (kmh == null || !Number.isFinite(kmh) ? '—' : `${Math.round(kmh)} km/h`)
